import { useCallback, useMemo } from 'react';
import { LocalStorageKey } from '@diritto/common/utils/localStorage';
import { TrackerExtraInfoProps, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { useTrackingState } from 'contexts/TrackingProvider';
import { useRequestSign } from 'ui-hooks/useRequestSign';
import { useAuthState } from 'contexts/AuthProvider';
import { useClient } from 'ui-hooks/useClient';
import { useShowAppPushOnBottomModal } from 'state-hooks/modal/useShowAppPushOnBottomModal';
import { LocalStorage } from 'utils/LocalStorage';
import { DirittoAppBridge } from 'libs/DirittoAppBridge';
import { showToast } from 'utils/modal';
import { useContentReactionStore } from 'stores/contents/ContentReactionStore';

export const useContentReaction = (contentId: number) => {
  const { tracker } = useTrackingState();
  const requestSign = useRequestSign();
  const { profile, isLoggedIn } = useAuthState();
  const { isApp } = useClient();

  const { snapshot, store } = useContentReactionStore();

  const showAppPushOnBottomModal = useShowAppPushOnBottomModal('푸시로 회차 등록 알림 받기');

  const favorite = useMemo(
    () => ({
      isFavorite: !!(profile && snapshot.contents?.[contentId]?.profiles[profile.id]?.favorite),
      favoriteCount: snapshot.contents?.[contentId]?.favoriteCount ?? 0,
    }),
    [contentId, profile, snapshot.contents],
  );
  const noti = useMemo(
    () => ({
      isNoti: !!(profile && snapshot.contents?.[contentId]?.profiles[profile.id]?.noti),
      notiCount: snapshot.contents?.[contentId]?.notiCount ?? 0,
    }),
    [contentId, profile, snapshot.contents],
  );

  const handleClickFavorite = useCallback(
    async ({ trackerExtraInfo }: TrackerExtraInfoProps) => {
      if (!isLoggedIn) {
        requestSign();
        return;
      }

      const nextFavorite = !favorite.isFavorite;
      if (nextFavorite) {
        showToast('좋아요한 작품에 추가했어요.');
        if (isApp && !LocalStorage.getItem(LocalStorageKey.InAppReviewRequestAt)) {
          LocalStorage.setItem(LocalStorageKey.InAppReviewRequestAt, new Date().toISOString());
          DirittoAppBridge.requestInAppReview();
        }

        const [favoriteResult, notiResult] = await Promise.allSettled([
          store.toggleFavorite(contentId, nextFavorite),
          store.toggleNoti(contentId, nextFavorite),
        ]);

        if (favoriteResult) {
          tracker.track(TrackingEventType.SetFavoriteContent, {
            contentId,
            profileId: Number(profile?.id),
            trackerExtraInfo,
          });
        }

        if (!favoriteResult && !notiResult) {
          showToast('좋아요에 실패했어요.');
        }
      } else {
        showToast('좋아요한 작품에서 제거했어요.');

        const [favoriteResult, notiResult] = await Promise.allSettled([
          store.toggleFavorite(contentId, nextFavorite),
          store.toggleNoti(contentId, nextFavorite),
        ]);

        if (favoriteResult) {
          tracker.track(TrackingEventType.ReleaseFavoriteContent, {
            contentId,
            profileId: profile?.id as number,
            trackerExtraInfo,
          });
        }

        if (!favoriteResult && !notiResult) {
          showToast('좋아요 해제에 실패했어요.');
        }
      }
    },
    [isLoggedIn, favorite.isFavorite, requestSign, store, contentId, tracker, profile?.id, isApp],
  );

  const handleClickNoti = useCallback(() => {
    if (!isLoggedIn) {
      requestSign();
      return;
    }

    const nextNoti = !noti.isNoti;
    if (nextNoti) {
      showToast('회차 등록 알림이 설정되었어요.', { hideCallback: showAppPushOnBottomModal });
      if (!store.toggleNoti(contentId, nextNoti)) {
        showToast('회차 등록 알림에 실패했어요.');
      }
    } else {
      showToast('회차 등록 알림이 해제되었어요.');
      if (!store.toggleNoti(contentId, nextNoti)) {
        showToast('회차 등록 알림 해제에 실패했어요.');
      }
    }
  }, [contentId, isLoggedIn, noti, requestSign, showAppPushOnBottomModal, store]);

  return useMemo(
    () => ({
      favorite,
      noti,
      toggleNoti: handleClickNoti,
      toggleFavorite: handleClickFavorite,
    }),
    [favorite, noti, handleClickNoti, handleClickFavorite],
  );
};
